/**
 * @file Submission UI — PublicationDetailsReadonlySection.
 * Publication Details on Dataset Overview — same stacked field UI as the publication form.
 */

import React from "react";
import "../../styles/formBase.css";
import "../../pages/Dashboard/Contributor/Stages/PublicationData/PublicationData.css";
import "./PublicationDetailsReadonlySection.css";
import PublicationDataFieldRenderer from "../../pages/Dashboard/Contributor/Stages/PublicationData/components/PublicationDataFieldRenderer";
import PublicationDataFieldGroups from "../../pages/Dashboard/Contributor/Stages/PublicationData/components/PublicationDataFieldGroups";
import type { FormField } from "../../interfaces/formInterfaces";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import {
  buildPublicationDetailsReadonlyObject,
  displayPublicationFieldValue,
  orderedPublicationFormFields,
  readPublicationValueForFormField,
  type PublicationAnyObj,
} from "./publicationDetailsReadonly";
import { stripFieldInfoText } from "./readonlyFormUtils";

const noopChange = () => {};

/** Type definition — Publication Details Readonly Section Props. */
export type PublicationDetailsReadonlySectionProps = {
  submission: PublicationAnyObj;
};

const PublicationDetailsReadonlySection: React.FC<
  PublicationDetailsReadonlySectionProps
> = ({ submission }) => {
  const formSchema: PublicationAnyObj[] = Array.isArray(submission.formSchema)
    ? submission.formSchema
    : [];

  const publication =
    submission.publication ??
    submission.publicationDetails ??
    null;

  const data = buildPublicationDetailsReadonlyObject(
    publication && typeof publication === "object"
      ? (publication as PublicationAnyObj)
      : null
  );

  const allFields = orderedPublicationFormFields(formSchema);

  if (!data) {
    return (
      <DatasetOverviewFormShell
        variant="publication"
        emptyMessage="No publication details available."
      />
    );
  }

  if (allFields.length === 0) {
    return (
      <DatasetOverviewFormShell
        variant="publication"
        emptyMessage="No publication fields configured."
      />
    );
  }

  const renderField = (field: FormField) => {
    const raw = readPublicationValueForFormField(data, field.name);
    const value =
      field.type === "DROPDOWN"
        ? raw != null && raw !== ""
          ? String(raw)
          : ""
        : displayPublicationFieldValue(field, data);

    return (
      <PublicationDataFieldRenderer
        key={field.id || field.name}
        field={stripFieldInfoText(field)}
        value={value}
        onChange={noopChange}
        disabled
      />
    );
  };

  return (
    <DatasetOverviewFormShell variant="publication">
      <PublicationDataFieldGroups
        allFields={allFields}
        renderField={renderField}
      />
    </DatasetOverviewFormShell>
  );
};

/** React component — Publication Details Readonly Section. */
export default PublicationDetailsReadonlySection;
